import { CircleMinus, Sparkle } from 'lucide-react'
import { SeoReportProductCard } from '~/src/components/reports/SeoReportProductCard'
import { SeoReportProductQuotes } from '~/src/components/reports/SeoReportProductQuotes'
import { SeoReportProductSpecs } from '~/src/components/reports/SeoReportProductSpecs'
import {
  LinkifyProductNames,
  type ProductLink,
} from '~/src/components/reports/LinkifyProductNames'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SeoRankedProductProps {
  slug: string
  rank: number
  name: string
  brand?: string | null
  roleLabel?: string | null
  roleDescription?: string | null
  images: string[]
  summary: string | null
  pricing: {
    msrp?: number | null
    observedLow?: number | null
    observedHigh?: number | null
    currency?: string | null
  }
  rating?: { score: number; count?: string | null } | null
  buyLinks: Array<{ label: string; url: string; price?: number | null }>
  specs: Array<{ label: string; value: string }>
  highlights: string[]
  worthKnowing: string[]
  communityQuotes: Array<{
    text: string
    source?: string
    url?: string | null
    platform?: 'tiktok' | 'instagram' | 'reddit' | 'youtube' | 'community'
  }>
  isTopPick?: boolean
  isImageChecking?: boolean
  onViewProduct?: (slug: string) => void
  /** Mapping of product names → first-buyLink URL, used to auto-link mentions. */
  productLinks?: ProductLink[]
  analyticsPosition?: number
}

// ---------------------------------------------------------------------------
// Section sub-components (kept inline -- small, tightly coupled to this view)
// ---------------------------------------------------------------------------

function ProductHeaderRow({ name, rank }: { name: string; rank: number }) {
  return (
    <div className='flex min-w-0 flex-1 flex-col-reverse gap-3 md:flex-row md:items-center md:justify-between'>
      <h2 className='min-w-0 flex-1 text-balance font-display text-[30px] font-bold leading-[38px] text-foreground'>
        {name}
      </h2>
      <div className='flex shrink-0 items-center gap-2'>
        <span className='whitespace-nowrap rounded-full bg-status-success-bg px-3 py-0.5 text-xs font-medium leading-6 text-status-success-text md:py-1 md:text-sm'>
          #{rank}
        </span>
      </div>
    </div>
  )
}

function Highlights({ items }: { items: string[] }) {
  if (items.length === 0) return null

  return (
    <div className='flex flex-col gap-3'>
      <h3 className='text-xl font-semibold leading-[30px] text-foreground'>
        Highlights
      </h3>
      <ul className='flex flex-col gap-2' role='list'>
        {items.map((item, index) => (
          <li key={`highlight-${index}`} className='flex items-start gap-2'>
            <Sparkle
              className='mt-1 size-4 shrink-0 text-info-500'
              fill='currentColor'
              strokeWidth={0}
            />
            <span className='text-pretty text-sm font-normal leading-6 text-foreground-secondary md:text-base'>
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function WorthKnowing({ items }: { items: string[] }) {
  if (items.length === 0) return null

  return (
    <div className='flex flex-col gap-3'>
      <h3 className='text-xl font-semibold leading-[30px] text-foreground'>
        Worth knowing
      </h3>
      <ul className='flex flex-col gap-2' role='list'>
        {items.map((item, index) => (
          <li key={`con-${index}`} className='flex items-start gap-2'>
            <CircleMinus className='mt-1 size-4 shrink-0 text-foreground-subtle' />
            <span className='text-pretty text-sm font-normal leading-6 text-foreground-secondary md:text-base'>
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SummaryParagraph({
  text,
  productLinks,
}: {
  text: string
  productLinks?: ProductLink[]
}) {
  return (
    <p className='text-base font-normal leading-6 text-foreground md:text-lg md:leading-7'>
      {productLinks && productLinks.length > 0 ? (
        <LinkifyProductNames text={text} products={productLinks} />
      ) : (
        text
      )}
    </p>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function SeoReportRankedProduct({
  slug,
  rank,
  name,
  brand: _brand, // reserved for future brand display
  roleLabel,
  roleDescription,
  images,
  summary,
  pricing,
  rating,
  buyLinks,
  specs,
  highlights,
  worthKnowing,
  communityQuotes,
  isTopPick = false,
  isImageChecking = false,
  onViewProduct,
  productLinks,
  analyticsPosition,
}: SeoRankedProductProps) {
  return (
    <section id={slug} className='flex scroll-mt-14 flex-col gap-4'>
      {/* 1. Header Row + Summary -- tighter spacing */}
      <div className='flex w-full items-center justify-between gap-3 text-left'>
        <ProductHeaderRow name={name} rank={rank} />
      </div>
      {summary && (
        <div className='relative z-10'>
          <SummaryParagraph text={summary} productLinks={productLinks} />
        </div>
      )}

      {/* Content -- always visible */}
      <div className='flex flex-col gap-10 pt-4'>
        {/* Product Card */}
        <SeoReportProductCard
          images={images}
          name={name}
          roleLabel={roleLabel}
          roleDescription={roleDescription}
          buyLinks={buyLinks}
          currency={pricing.currency}
          slug={slug}
          isTopPick={isTopPick}
          isImageChecking={isImageChecking}
          onViewProduct={onViewProduct}
          analyticsSection='ranked-product'
          analyticsPosition={analyticsPosition}
        />

        {/* Key specs (top 8) -- "see details" opens the drawer with full list */}
        <SeoReportProductSpecs
          specs={specs}
          rating={rating ?? null}
          onSeeAll={onViewProduct ? () => onViewProduct(slug) : undefined}
        />

        {/* Highlights (Pros) -- always visible */}
        <Highlights items={highlights} />

        {/* Worth Knowing (Cons) -- always visible */}
        <WorthKnowing items={worthKnowing} />

        {/* What People Are Saying -- always visible */}
        <SeoReportProductQuotes quotes={communityQuotes} />
      </div>
    </section>
  )
}
